<template>
  <ExportPdf
    ref="exportPdf"
    :filename="generateFileName('report')"
    @hasDownloaded="hasDownloaded"
  >
    <b-container>
      <BaseFilterToolbar @reset="initialize" padding="10px 0 0 0">
        <TheTitle>統計報告</TheTitle>
        <DropDown
          v-if="user.class !== 'NORMAL'"
          v-model="filter.paymentType"
          :options="couponTypeOptions"
          class="mr-1 pt-2"
        ></DropDown>
        <template v-slot:end>
          <div class="d-flex align-items-center">
            <SearchBar
              v-if="role === 'admin' || role === 'sale'"
              placeholder="搜尋商戶名稱"
              v-model.trim="keyword"
              class="mr-1"
            ></SearchBar>
            <DateTimePicker
              v-model="dateRange"
              has-range
              has-shortcuts
              placeholder="選擇日期"
              :max="addDays(new Date(), 0)">
            </DateTimePicker>
            <label>
              <BaseButton @click="exportPdf" data-html2canvas-ignore>匯出</BaseButton>
            </label>
          </div>
        </template>
      </BaseFilterToolbar>
      <!-- mobile start -->
      <div class="app--hidden app--mobile-shown">
        <div>
          <DropDown
            v-if="user.class !== 'NORMAL'"
            v-model="filter.paymentType"
            :options="couponTypeOptions"
            class="mr-1 pt-2 dropdown-style"
          ></DropDown>
        </div>
        <div class="top-right-corner" data-html2canvas-ignore>
          <div class="app--hidden app--mobile-shown--inline-block">
            <DateTimePicker
              v-model="dateRange"
              has-range
              has-shortcuts
              placeholder=""
              :max="addDays(new Date(), 0)">
              <template v-slot:icon>
              <img src="@/assets/img/icon/calendar-mobile.svg"
                alt="search-icon"
                style="cursor: pointer;"
              /></template>
            </DateTimePicker>
          </div>
          <div class="app--hidden app--mobile-shown--inline-block">
            <img
              src="@/assets/img/icon/export.svg"
              alt="export icon"
              style="cursor: pointer;"
              @click="exportPdf"
            />
          </div>
        </div>
      </div>
      <!-- mobile end -->
      <Deal :dateRange="dateRange" :filter="filter" :keyword="keyword" v-model="interval"> </Deal>
      <div :class="{'html2pdf__page-break': isShowTopFiveViewResult}">
        <TheTitle>
          <img src="@/assets/img/statistics/list.svg" alt="deal" class="mr-1" />列表
        </TheTitle>
        <h2 v-if="user.class === 'NORMAL'">Top 3 優惠</h2>
        <h2 v-else>Top 5 優惠</h2>
        <div class="datatable mb-3">
          <DataTable
            v-show="topXResult"
            :fields="fields"
            :mobileFields="mobileFields"
            :items="topXResult"
            :options.sync="options"
            disableItemPerPage
            @row-clicked="goToItem"
            showRowDetails
          >
            <template v-slot:cell(rewardId)="{ value }">
              <router-link :to="{ path:
                `${role === 'merchant' ? '' : `/${role}`}/${paymentTypePath}/${value}`
              }">
              {{ value }}</router-link>
            </template>
            <template v-slot:cell(rewardName)="{ item }">

              <div class="d-flex">
                <img v-if="item.photo" class="overview-item__cover mr-2"
                :src="item.photo | imagePath('couponCover')">
                <div class="d-flex flex-column rewardName">
                  {{ item.rewardName }}
                </div>
              </div>
            </template>
          </DataTable>
        </div>
      </div>
      <div class="html2pdf__page-break" v-if="isShowTopFiveViewResult">
        <TheTitle>
          <img src="@/assets/img/statistics/browse.svg" alt="deal" class="mr-1" />瀏覽數據
        </TheTitle>
        <h2>五大優惠瀏覽數目</h2>
        <DonutChart v-if="topFiveViewResult"
          :chartdata="topFiveViewResult"
          third-column-name="瀏覽次數">
        </DonutChart>
      </div>
      <div v-if="chartdataResult && role === 'admin'">
        <TheTitle>
          <img src="@/assets/img/statistics/cate.svg" alt="deal" class="mr-1" />分類
        </TheTitle>
        <h2>產品分類</h2>
        <DonutChart v-if="chartdataResult"
          :chartdata="chartdataResult">
        </DonutChart>
      </div>
    </b-container>
  </ExportPdf>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, switchMap, debounceTime, pluck, mergeAll, filter,
} from 'rxjs/operators';
import store, { SET_LOADING } from '@/store';

import { BContainer } from 'bootstrap-vue';
import { addDays, getWeekEndDate, formatDate } from '@/helpers/date';
import DateTimePicker from '@/components/DateTimePicker.vue';
import TheTitle from '../../components/TheTitle.vue';
import Deal from '../../components/statistics/Deal.vue';
import DonutChart from '../../components/chart/DonutChart.vue';
import BaseButton from '../../components/BaseButton.vue';
import SearchBar from '../../components/SearchBar.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';
import DataTable from '../../components/DataTable.vue';
import dataTableMixin from '../../mixins/data-table';
import exportMixin from '../../mixins/export';
import imagePath from '../../filters/image-path';
import DropDown from '../../components/DropDown.vue';
import ExportPdf from '../../components/ExportPdf.vue';

const UFUN_NORMAL_HEADERS = [
  { key: 'rewardId', label: '商品編號', sortable: false },
  { key: 'rewardName', label: '優惠名稱', sortable: false },
  { key: 'totalItems', label: '已兌換數目', sortable: false },
  { key: 'totalUsed', label: '已使用數目', sortable: false },
];

const UFUN_HEADERS = [
  { key: 'rewardId', label: '商品編號', sortable: false },
  { key: 'rewardName', label: '優惠名稱', sortable: false },
  { key: 'totalItems', label: '已兌換數目', sortable: false },
  { key: 'totalUsed', label: '已使用數目', sortable: false },
  {
    key: 'redeemRate',
    label: '兌換率',
    sortable: false,
    formatter: (value) => (value !== '-' ? (value * 100).toString().concat('%') : value),
  },
];
const HKD_HEADERS = [

  { key: 'rewardId', label: '商品編號', sortable: false },
  { key: 'rewardName', label: '優惠名稱', sortable: false },
  { key: 'salesTotal', label: '成交總額', sortable: false },
  { key: 'totalOrders', label: '成交訂單總數', sortable: false },
  { key: 'profitTotal', label: '銷售總額', sortable: false },
  { key: 'totalItems', label: '已兌換數目', sortable: false },
  { key: 'totalUsed', label: '已使用數目', sortable: false },
  {
    key: 'redeemRate',
    label: '兌換率',
    sortable: false,
    formatter: (value) => (value !== '-' ? (value * 100).toString().concat('%') : value),
  },
];

const MOBILE_HEADER = [
  {
    key: 'rewardName',
    label: '優惠名稱',
  },
];

export default {
  mixins: [dataTableMixin, exportMixin],
  components: {
    BContainer,
    BaseButton,
    TheTitle,
    DropDown,
    Deal,
    DateTimePicker,
    SearchBar,
    BaseFilterToolbar,
    DataTable,
    DonutChart,
    ExportPdf,
  },
  data: () => ({
    couponTypeOptions: [
      { value: 'UFUN', text: '積分優惠券', img: 'icon/coupon.svg' },
      { value: 'HKD', text: '付款優惠券', img: 'icon/paid-coupon.svg' },
    ],
    filter: {
      paymentType: 'UFUN',
      startDate: formatDate(addDays(getWeekEndDate(), -21)),
      endDate: formatDate(getWeekEndDate()),
    },
    options: {
      // sortBy: 'id',
    },
    dateRange: null,
    keyword: '',
    interval: {
      redemption: 'week',
      usedCoupon: 'week',
      sales: 'week',
      profit: 'week',
      completedOrder: 'week',
    },
    mobileFields: MOBILE_HEADER,
  }),
  methods: {
    addDays,
    initialize() {
      const defaultStartDate = formatDate(addDays(getWeekEndDate(), -21));
      const defaultEndDate = formatDate(getWeekEndDate());
      // only reset if is not the default value
      if (this.filter.startDate !== defaultStartDate && this.filter.endDate !== defaultEndDate) {
        this.dateRange = [defaultStartDate, defaultEndDate];
      }
      this.keyword = '';
      this.interval = {
        redemption: 'week',
        usedCoupon: 'week',
        sales: 'week',
        profit: 'week',
        completedOrder: 'week',
      };
    },
    exportPdf() {
      store.commit(SET_LOADING, true);
      this.$refs.exportPdf.generateReport();
    },
    hasDownloaded() {
      store.commit(SET_LOADING, false);
    },
    goToItem({ rewardId }) {
      this.$router.push({
        path: `${this.role === 'merchant' ? '' : `/${this.role}`}/${this.paymentTypePath}/${rewardId}`,
      });
    },
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['role']),
    fields() {
      if (this.user.class === ('NORMAL')) {
        return UFUN_NORMAL_HEADERS;
      }
      return this.filter.paymentType === 'UFUN' ? UFUN_HEADERS : HKD_HEADERS;
    },
    isShowTopFiveViewResult() {
      return this.topFiveViewResult && this.role === 'admin';
    },
    paymentTypePath() {
      return this.filter.paymentType === 'UFUN' ? 'coupons' : 'paidCoupons';
    },
  },
  filters: {
    imagePath,
  },
  subscriptions() {
    const chartdata$ = (data) => this.httpRequest$(
      {
        method: 'GET',
        url: `/agc-${this.role}-api/statistic/rewardRpt`,
      },
      data,
    );
    const topX$ = (data) => this.httpRequest$(
      {
        method: 'GET',
        url: `/agc-${this.role}-api/statistic/topXOfRedemption`,
      },
      data,
    );
    const topFiveView$ = (data) => this.httpRequest$(
      {
        method: 'GET',
        url: `/agc-${this.role}-api/statistic/topFiveViewReward`,
      },
      data,
    );
    return {
      chartdataResult: scheduled(
        [...this.scheduleTasks],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        filter(() => this.role === 'admin' || this.role === 'sale'),
        tap(() => {
          store.commit(SET_LOADING, true);
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(chartdata$),
      ),

      topXResult: scheduled(
        [...this.scheduleTasks],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          store.commit(SET_LOADING, true);
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(topX$),
      ),

      topFiveViewResult: scheduled(
        [...this.scheduleTasks],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        filter(() => this.role === 'admin' || this.role === 'sale'),
        tap(() => {
          store.commit(SET_LOADING, true);
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(topFiveView$),
      ),
    };
  },
};
</script>

<style lang="scss" scoped>

.datatable {
    padding: 1rem;
    background-color: #fff;
    border: #dde1e5 1px solid;
    border-radius: 8px;
}

.overview-item {
  &__cover {
    width: 50px;
    height: 50px;
  }
}

.top-right-corner {
  position: fixed;
  top: 6px;
  z-index:101;

  @include respond(tab-landscape)  {
    right: 13rem;
  }

  @include respond(phone) {
    right: 6rem;
  }
}

.rewardName {
  display: inline-block !important;
}

::v-deep {
  @include respond(tab-landscape) {
    .mx-input {
      border: none;
      background: none;
      box-shadow: none;
      opacity: 0;
      font-size: 0px;  // fix misalignment of mobile icons at the top right corner
    }
    .dropdown-style{
      display: block;
      text-align: center;
      width: 100%;
    }
  }
  .dropdown-style {
    @include respond(tab-landscape)  {
       margin-top: 7%;
    }

    @include respond(phone) {
      margin-top: 10%;
    }
  }
}
</style>
